import moment from 'moment'

import {ClockSystem} from './types'

export const getClockSystem = (): ClockSystem => {
  const currentTime = moment().format('LT').toLocaleUpperCase()
  const has12HoursFormat = currentTime.endsWith('AM') || currentTime.endsWith('PM')
  return has12HoursFormat ? ClockSystem['12H'] : ClockSystem['24H']
}

export const getTimeOffset = (): number => (getClockSystem() === ClockSystem['12H'] ? 12 : 0)

export const isStartOfDay = (time: string): boolean => {
  return moment().startOf('day').format('HH:mm:ss') === time
}

export const isEndOfDay = (time: string): boolean => {
  return moment().endOf('day').format('HH:mm:ss') === time
}

export const getValidHours = (minTime?: string, maxTime?: string): string[] => {
  const has12HoursFormat = getClockSystem() === ClockSystem['12H']
  const timeFormat = 'hh:mm'
  const allHours =
    getClockSystem() === ClockSystem['12H']
      ? [12, ...Array(11).keys()].map((i) => String(i === 12 ? i : i + 1))
      : [...Array(24).keys()].map((i) => String(i))

  if (minTime && maxTime && (!isStartOfDay(minTime) || !isEndOfDay(maxTime))) {
    if (has12HoursFormat) {
      // For 12-hour format, we need to handle AM/PM separately
      // Since hours are now just numbers 1-12, we return all hours
      // and let the AM/PM constraint be handled by getAvailableUnits
      return allHours
    } else {
      const minLoadHour = moment(minTime, timeFormat).format('H')
      const maxLoadHour = moment(maxTime, timeFormat).format('H')
      return allHours.slice(allHours.indexOf(minLoadHour), allHours.indexOf(maxLoadHour) + 1)
    }
  }
  return allHours
}

export const combineDateAndTime = (
  date: moment.Moment,
  time?: string,
  clockSystem?: ClockSystem
): moment.Moment => {
  const has12HoursFormat = (clockSystem ?? getClockSystem()) === ClockSystem['12H']
  const timeFormat = has12HoursFormat ? 'hh:mm' : 'HH:mm'
  const dateMoment = moment(date)
  return moment(time, timeFormat)
    .date(dateMoment.date())
    .month(dateMoment.month())
    .year(dateMoment.year())
}

export const getAvailableUnits = (minTime?: string, maxTime?: string): string[] => {
  if (getClockSystem() === ClockSystem['24H']) return []
  if (!minTime || !maxTime) return ['AM', 'PM']
  return Array.from(
    new Set([
      combineDateAndTime(moment(), minTime, ClockSystem['24H']).format('A'),
      combineDateAndTime(moment(), maxTime, ClockSystem['24H']).format('A')
    ])
  )
}

export const getHourForIndex = (columns: any[], index: number): number =>
  parseInt(columns[0].values[index] as string, 10)

export const getMinutesForIndex = (columns: any[], index: number): number =>
  parseInt(columns[1].values[index] as string, 10)

export const getTimeUnitForIndexOrUndefined = (
  columns: any[],
  index: number | undefined
): string | undefined => {
  const has12HoursFormat = getClockSystem() === ClockSystem['12H']
  return has12HoursFormat && index !== undefined ? (columns[2].values[index] as string) : undefined
}

export const updateDate = (
  date: moment.Moment,
  selectedHours: number,
  selectedMinutes: number,
  selectedTimeUnit: string | undefined
): moment.Moment => {
  const isMorning = selectedTimeUnit === 'AM'
  const isAfterNoon = selectedTimeUnit === 'PM'

  const normalizedHours = (() => {
    if (isAfterNoon) {
      return selectedHours === 12 ? selectedHours : selectedHours + getTimeOffset()
    } else if (isMorning) {
      return selectedHours === 12 ? 0 : selectedHours
    }
    return selectedHours
  })()

  return moment(date).hours(normalizedHours).minutes(selectedMinutes)
}

export const getMinDateTime = (minDate: moment.Moment, minTime?: string): moment.Moment => {
  return combineDateAndTime(minDate, minTime, ClockSystem['24H'])
}

export const getMaxDateTime = (maxDate: moment.Moment, maxTime?: string): moment.Moment => {
  return combineDateAndTime(maxDate, maxTime, ClockSystem['24H'])
}

export const normalizeTime = (
  time: moment.Moment,
  minTime: moment.Moment,
  maxTime: moment.Moment
): moment.Moment => {
  const isBeforeMin = time.isBefore(minTime)
  const isAfterMax = time.isAfter(maxTime)
  if (isBeforeMin) {
    return minTime
  }
  if (isAfterMax) {
    return maxTime
  }
  return time
}

export const filterHours = (hours: string[], unit: string): string[] => {
  return [11, ...Array(11).keys()]
    .map((i) => String(i + 1))
    .filter((i) => {
      return hours.includes(i + unit)
    })
}
